/**
 * Homepage full-viewport project slideshow
 * Decoupled from .g-carousel / project media carousels.
 *
 * Caption link → project page
 * Image click left 25% → previous slide
 * Image click right 75% → next slide
 * Transition: fast fade (no horizontal slide)
 */
import { whenHomeIntroDone, HOME_INTRO_DONE_EVENT } from "./ma-logo-scroll";
import { preloadLazyMedia, bindFeaturedCoverFades } from "../utils/lazy-media";

const TRANSITION_MS = 440;
const AUTOPLAY_MS = 6000;
const INTRO_AUTOPLAY_FALLBACK_MS = 4000;
const PREV_ZONE_RATIO = 0.25;

export function initHomeSlideshow() {
  const slideshow = document.querySelector("[data-home-slideshow]");

  if (!slideshow) {
    return;
  }

  if (window.main?.homeSlideshowCleanup) {
    window.main.homeSlideshowCleanup();
  }

  const track = slideshow.querySelector(".c-home-slideshow__track");
  const slides = slideshow.querySelectorAll(".c-home-slideshow__slide");
  const caption = slideshow.querySelector(".c-home-slideshow__caption");
  const captionText = slideshow.querySelector(".c-home-slideshow__caption-text");

  if (!track || slides.length === 0) {
    return;
  }

  // Preload every cover so autoplay / swipe never shows blur-up on inactive slides.
  preloadLazyMedia(slideshow);
  bindFeaturedCoverFades(slideshow);

  slideshow.setAttribute("aria-live", "polite");

  const slideMeta = Array.from(slides).map((slide) => ({
    caption: (slide.getAttribute("data-caption") || "").trim(),
    href: slide.getAttribute("data-href") || "",
    colorKey: slide.getAttribute("data-color-combination") || "",
    colorAccent: slide.getAttribute("data-project-accent") || "",
    colorAccentText: slide.getAttribute("data-project-accent-text") || "",
  }));

  let currentIndex = 0;
  const totalSlides = slides.length;
  let autoplayActive = false;
  let autoplayTimeout = null;
  let touchStartX = 0;
  let isAnimating = false;
  let suppressClick = false;
  let animLockTimer = null;
  let introAllowsAutoplay = false;

  const controller = new AbortController();
  const { signal } = controller;

  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const clearLeavingSlides = () => {
    slides.forEach((slide) => {
      slide.classList.remove("is-leaving");
    });
  };

  const updateSlideVisibility = (activeIndex, { previousIndex = null, instant = false } = {}) => {
    const crossfade =
      !instant &&
      !prefersReducedMotion() &&
      previousIndex !== null &&
      previousIndex !== activeIndex;

    if (crossfade) {
      // Keep outgoing fully opaque under the incoming fade (no simultaneous fade-out).
      slides.forEach((slide, index) => {
        const isLeaving = index === previousIndex;
        slide.classList.toggle("is-leaving", isLeaving);
        if (isLeaving) {
          slide.classList.remove("is-active");
          slide.setAttribute("aria-hidden", "true");
        }
      });

      // Force a paint so the leaving slide is solid before the fade-in starts.
      void slideshow.offsetWidth;

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;
        slide.classList.toggle("is-active", isActive);
        if (isActive) {
          slide.classList.remove("is-leaving");
          slide.setAttribute("aria-hidden", "false");
        } else if (index !== previousIndex) {
          slide.classList.remove("is-leaving");
          slide.setAttribute("aria-hidden", "true");
        }
      });
      return;
    }

    slides.forEach((slide, index) => {
      const isActive = index === activeIndex;
      slide.classList.toggle("is-active", isActive);
      slide.classList.remove("is-leaving");
      slide.setAttribute("aria-hidden", isActive ? "false" : "true");
    });
  };

  const syncCaption = () => {
    const meta = slideMeta[currentIndex];
    if (!meta) {
      return;
    }

    if (captionText) {
      captionText.textContent = meta.caption;
    }
    if (caption) {
      if (meta.href) {
        caption.setAttribute("href", meta.href);
      } else {
        caption.removeAttribute("href");
      }

      if (meta.colorKey) {
        caption.setAttribute("data-color-combination", meta.colorKey);
        caption.setAttribute("data-project-accent", meta.colorAccent);
        caption.setAttribute("data-project-accent-text", meta.colorAccentText);
      } else {
        caption.removeAttribute("data-color-combination");
        caption.removeAttribute("data-project-accent");
        caption.removeAttribute("data-project-accent-text");
      }
    }
  };

  const updateSlideshow = ({ previousIndex = null, instant = false } = {}) => {
    syncCaption();
    updateSlideVisibility(currentIndex, { previousIndex, instant });
  };

  const scheduleAutoplayTick = () => {
    if (autoplayTimeout) {
      clearTimeout(autoplayTimeout);
      autoplayTimeout = null;
    }

    if (!autoplayActive || totalSlides <= 1 || document.hidden) {
      return;
    }

    autoplayTimeout = window.setTimeout(() => {
      autoplayTimeout = null;

      if (!autoplayActive || totalSlides <= 1 || document.hidden) {
        return;
      }

      nextSlide();
    }, AUTOPLAY_MS);
  };

  const unlockAnimation = () => {
    isAnimating = false;
    if (animLockTimer) {
      clearTimeout(animLockTimer);
      animLockTimer = null;
    }

    // Drop outgoing slide only after incoming is fully opaque
    clearLeavingSlides();

    if (autoplayActive && !autoplayTimeout) {
      scheduleAutoplayTick();
    }
  };

  const lockAnimation = () => {
    if (animLockTimer) {
      clearTimeout(animLockTimer);
      animLockTimer = null;
    }

    isAnimating = true;
    animLockTimer = window.setTimeout(unlockAnimation, TRANSITION_MS + 50);
  };

  const goToSlide = (index, { instant = false } = {}) => {
    if (totalSlides <= 1) {
      return;
    }

    if (isAnimating && !instant) {
      return;
    }

    let nextIndex = index;
    if (index < 0) {
      nextIndex = totalSlides - 1;
    } else if (index >= totalSlides) {
      nextIndex = 0;
    }

    if (nextIndex === currentIndex && !instant) {
      return;
    }

    const previousIndex = currentIndex;
    currentIndex = nextIndex;
    updateSlideshow({ previousIndex, instant });

    if (instant || prefersReducedMotion()) {
      unlockAnimation();
      if (autoplayActive && !autoplayTimeout) {
        scheduleAutoplayTick();
      }
      return;
    }

    lockAnimation();
  };

  const nextSlide = () => goToSlide(currentIndex + 1);
  const prevSlide = () => goToSlide(currentIndex - 1);

  const stopAutoplay = () => {
    autoplayActive = false;
    if (autoplayTimeout) {
      clearTimeout(autoplayTimeout);
      autoplayTimeout = null;
    }
  };

  const startAutoplay = () => {
    if (totalSlides <= 1 || !introAllowsAutoplay || document.hidden) {
      return;
    }

    autoplayActive = true;

    if (!isAnimating && !autoplayTimeout) {
      scheduleAutoplayTick();
    }
  };

  const restartAutoplay = () => {
    stopAutoplay();
    startAutoplay();
  };

  const enableAutoplayAfterIntro = () => {
    if (controller.signal.aborted || introAllowsAutoplay) {
      return;
    }

    introAllowsAutoplay = true;
    startAutoplay();
  };

  const onIntroDone = () => {
    if (controller.signal.aborted) {
      return;
    }
    enableAutoplayAfterIntro();
  };

  const navDirectionFromClientX = (clientX) => {
    const rect = slideshow.getBoundingClientRect();
    if (rect.width <= 0) {
      return "next";
    }

    const ratio = (clientX - rect.left) / rect.width;
    return ratio < PREV_ZONE_RATIO ? "prev" : "next";
  };

  const onKeyDown = (event) => {
    if (totalSlides <= 1) {
      return;
    }

    // Ignore when focus is in a form field
    const tag = event.target?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || event.target?.isContentEditable) {
      return;
    }

    if (event.key === "ArrowRight") {
      nextSlide();
      restartAutoplay();
    }

    if (event.key === "ArrowLeft") {
      prevSlide();
      restartAutoplay();
    }
  };

  const onTouchStart = (event) => {
    touchStartX = event.changedTouches[0].screenX;
  };

  const onTouchEnd = (event) => {
    if (totalSlides <= 1) {
      return;
    }

    const touchEndX = event.changedTouches[0].screenX;
    const delta = touchEndX - touchStartX;

    if (Math.abs(delta) < 40) {
      return;
    }

    // Prevent the synthetic click that follows a swipe
    suppressClick = true;

    if (delta < 0) {
      nextSlide();
    } else {
      prevSlide();
    }

    restartAutoplay();
  };

  // Image / track click: left 25% → prev, right 75% → next.
  // Caption link still navigates to the project.
  const onSlideshowClick = (event) => {
    if (suppressClick) {
      suppressClick = false;
      return;
    }

    if (totalSlides <= 1) {
      return;
    }

    if (event.target.closest(".c-home-slideshow__caption")) {
      return;
    }

    // Don't steal clicks from interactive media controls if any
    if (event.target.closest("a, button")) {
      return;
    }

    if (navDirectionFromClientX(event.clientX) === "prev") {
      prevSlide();
    } else {
      nextSlide();
    }

    restartAutoplay();
  };

  const onVisibilityChange = () => {
    if (document.hidden) {
      if (autoplayTimeout) {
        clearTimeout(autoplayTimeout);
        autoplayTimeout = null;
      }
      return;
    }

    if (introAllowsAutoplay) {
      autoplayActive = true;
      if (!isAnimating && !autoplayTimeout) {
        scheduleAutoplayTick();
      }
    }
  };

  const onTransitionEnd = (event) => {
    if (
      !event.target?.classList?.contains("c-home-slideshow__slide") ||
      !event.target.classList.contains("is-active") ||
      event.propertyName !== "opacity"
    ) {
      return;
    }

    unlockAnimation();
  };

  const canHoverNavHint = () =>
    window.matchMedia("(hover: hover) and (pointer: fine)").matches;

  const clearNavHint = () => {
    slideshow.removeAttribute("data-nav-hint");
  };

  const onPointerMove = (event) => {
    if (totalSlides <= 1 || !canHoverNavHint()) {
      return;
    }

    if (event.target.closest(".c-home-slideshow__caption")) {
      clearNavHint();
      return;
    }

    slideshow.setAttribute("data-nav-hint", navDirectionFromClientX(event.clientX));
  };

  slideshow.addEventListener("touchstart", onTouchStart, { passive: true, signal });
  slideshow.addEventListener("touchend", onTouchEnd, { passive: true, signal });
  slideshow.addEventListener("click", onSlideshowClick, { signal });
  slideshow.addEventListener("pointermove", onPointerMove, { passive: true, signal });
  slideshow.addEventListener("pointerleave", clearNavHint, { signal });
  track.addEventListener("transitionend", onTransitionEnd, { signal });
  document.addEventListener("keydown", onKeyDown, { signal });
  document.addEventListener("visibilitychange", onVisibilityChange, { signal });

  slideshow.dataset.initialized = "true";

  // Drop any inline styles Swup-cached DOM may carry; always start at slide 0.
  currentIndex = 0;
  isAnimating = false;
  track.style.removeProperty("transform");
  track.style.removeProperty("transition");
  updateSlideshow();

  const scheduleIntroAutoplayFallback = () => {
    window.setTimeout(() => {
      if (!controller.signal.aborted) {
        onIntroDone();
      }
    }, INTRO_AUTOPLAY_FALLBACK_MS);
  };

  // Sync in case intro finished before this init (Swup return to home)
  if (document.documentElement.classList.contains("ma-logo-intro-done")) {
    onIntroDone();
  } else {
    whenHomeIntroDone().then(onIntroDone);
    document.addEventListener(HOME_INTRO_DONE_EVENT, onIntroDone, {
      once: true,
      signal,
    });
    scheduleIntroAutoplayFallback();
  }

  window.main = window.main || {};
  window.main.homeSlideshowCleanup = () => {
    stopAutoplay();
    unlockAnimation();
    controller.abort();
    delete slideshow.dataset.initialized;
    if (window.main?.homeSlideshowCleanup) {
      window.main.homeSlideshowCleanup = null;
    }
  };
}
